"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SignalTiesDenormalizedSchema = exports.ExtractedSignalTieSchema = exports.ComponentValueTypeSchema = exports.ComponentValueType = exports.SignalTieApplicabilitySchema = exports.SignalTieApplicability = void 0;
exports.getDenormalizedSignalTiesFromSignals = getDenormalizedSignalTiesFromSignals;
exports.applyDenormalizedSignalTiesToSignals = applyDenormalizedSignalTiesToSignals;
exports.normalizeComponentValue = normalizeComponentValue;
exports.convertZeroComponentValueToNull = convertZeroComponentValueToNull;
exports.convertZeroValuesToNullInSignalTies = convertZeroValuesToNullInSignalTies;
exports.normalizeAndDeduplicateSignalTies = normalizeAndDeduplicateSignalTies;
exports.deduplicateSignalTies = deduplicateSignalTies;
const zod_1 = require("zod");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const SignalTies_1 = require("../../SignalTies");
var SignalTieApplicability;
(function (SignalTieApplicability) {
    SignalTieApplicability["Required"] = "Required";
    SignalTieApplicability["Recommended"] = "Recommended";
    SignalTieApplicability["Conditional"] = "Conditional";
})(SignalTieApplicability || (exports.SignalTieApplicability = SignalTieApplicability = {}));
exports.SignalTieApplicabilitySchema = zod_1.z.nativeEnum(SignalTieApplicability);
var ComponentValueType;
(function (ComponentValueType) {
    ComponentValueType["Absolute"] = "absolute";
    ComponentValueType["Minimum"] = "minimum";
    ComponentValueType["Maximum"] = "maximum";
    ComponentValueType["Range"] = "range";
})(ComponentValueType || (exports.ComponentValueType = ComponentValueType = {}));
exports.ComponentValueTypeSchema = zod_1.z.nativeEnum(ComponentValueType);
exports.ExtractedSignalTieSchema = zod_1.z
    .object({
    toSignalName: zod_1.z.string(),
    connectionType: zod_1.z.nativeEnum(SignalTies_1.SignalTieConnectionType),
    componentType: zod_1.z.nativeEnum(SignalTies_1.SignalTieComponentType).nullable(),
    componentValue: zod_1.z.string().nullable(),
    componentValueVariance: zod_1.z.string().nullable(),
    evidence: zod_1.z.string(),
})
    .strict();
exports.SignalTiesDenormalizedSchema = zod_1.z
    .object({
    signalName: zod_1.z.string(),
    ties: zod_1.z.array(exports.ExtractedSignalTieSchema),
})
    .strict();
function getDenormalizedSignalTiesFromSignals(signals, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
    return signals.map((signal) => {
        const ties = [];
        if (signal.ties) {
            for (const externalComponent of signal.ties) {
                const flatTie = (0, SignalTies_1.convertExternalComponentToFlatSignalTie)(externalComponent, signals, logger);
                if (flatTie) {
                    const extractedTie = flatTieToExtractedTie(flatTie, signals, logger);
                    ties.push(extractedTie);
                }
            }
        }
        return {
            signalName: signal.name,
            ties,
        };
    });
}
function applyDenormalizedSignalTiesToSignals(signals, signalTies, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
    const tiesMap = new Map();
    const availableSignalNames = signals.map((s) => s.name);
    for (const signalTie of signalTies) {
        const matchingSignal = signals.find((s) => s.name === signalTie.signalName);
        if (!matchingSignal) {
            logger.warn('ApplyDenormalizedSignalTiesToSignals_SignalNotFound', {
                signalName: signalTie.signalName,
                availableSignalNames,
            });
            continue;
        }
        if (matchingSignal.ties && matchingSignal.ties.length > 0 && signalTie.ties.length > 0) {
            logger.warn('ApplyDenormalizedSignalTiesToSignals_OverwritingExistingTies', {
                signalName: matchingSignal.name,
                existingTiesCount: matchingSignal.ties.length,
                newTiesCount: signalTie.ties.length,
            });
        }
        tiesMap.set(signalTie.signalName, signalTie.ties);
    }
    return signals.map((signal) => {
        const extractedTies = tiesMap.get(signal.name);
        if (extractedTies === undefined) {
            return signal;
        }
        const externalComponents = [];
        for (const extractedTie of extractedTies) {
            const flatTie = extractedTieToFlatTie(extractedTie, signals, logger);
            if (!flatTie) {
                continue;
            }
            try {
                const externalComponent = (0, SignalTies_1.convertFlatSignalTieToExternalComponent)(flatTie, signals, logger);
                externalComponents.push(externalComponent);
            }
            catch (error) {
                logger.warn('ApplyDenormalizedSignalTiesToSignals_ConversionFailed', {
                    signalName: signal.name,
                    extractedTie,
                    error: error instanceof Error ? error.message : String(error),
                });
            }
        }
        return {
            ...signal,
            ties: externalComponents.length > 0 ? externalComponents : undefined,
        };
    });
}
function flatTieToExtractedTie(flatTie, signals, logger) {
    const toSignalName = sigma_engine_shared_1.DesignModels.findSignalNameBySignalId(flatTie.toSignalId, signals, logger) ?? flatTie.toSignalId;
    if (flatTie.connectionType === SignalTies_1.SignalTieConnectionType.Direct) {
        return {
            toSignalName,
            connectionType: flatTie.connectionType,
            componentType: null,
            componentValue: null,
            componentValueVariance: null,
            evidence: '',
        };
    }
    if (!flatTie.componentType) {
        logger.warn('FlatTieToExtractedTie_MissingComponentType', { flatTie });
        return {
            toSignalName,
            connectionType: flatTie.connectionType,
            componentType: null,
            componentValue: null,
            componentValueVariance: null,
            evidence: '',
        };
    }
    if (flatTie.componentValue === null) {
        return {
            toSignalName,
            connectionType: flatTie.connectionType,
            componentType: flatTie.componentType,
            componentValue: null,
            componentValueVariance: null,
            evidence: '',
        };
    }
    const units = SignalTies_1.TIE_COMPONENT_TYPE_TO_UNITS[flatTie.componentType];
    if (flatTie.componentValue.type === 'absolute') {
        const componentValue = `${sigma_engine_shared_1.Utils.formatNumericalSIValue(flatTie.componentValue.value)}${units.short}`;
        let componentValueVariance = null;
        if (flatTie.componentValue.variance !== null && flatTie.componentValue.varianceType !== null) {
            if (flatTie.componentValue.varianceType === SignalTies_1.SignalTieVarianceType.Relative) {
                componentValueVariance = `${flatTie.componentValue.variance}%`;
            }
            else {
                componentValueVariance = `${sigma_engine_shared_1.Utils.formatNumericalSIValue(flatTie.componentValue.variance)}${units.short}`;
            }
        }
        return {
            toSignalName,
            connectionType: flatTie.connectionType,
            componentType: flatTie.componentType,
            componentValue,
            componentValueVariance,
            evidence: '',
        };
    }
    if (flatTie.componentValue.type === 'range') {
        return {
            toSignalName,
            connectionType: flatTie.connectionType,
            componentType: flatTie.componentType,
            componentValue: null,
            componentValueVariance: null,
            evidence: '',
        };
    }
    logger.warn('FlatTieToExtractedTie_UnknownComponentValueType', { flatTie });
    return {
        toSignalName,
        connectionType: flatTie.connectionType,
        componentType: flatTie.componentType,
        componentValue: null,
        componentValueVariance: null,
        evidence: '',
    };
}
function extractedTieToFlatTie(extractedTie, signals, logger) {
    const toSignalId = sigma_engine_shared_1.DesignModels.findSignalIdBySignalName(extractedTie.toSignalName, signals, logger);
    if (!toSignalId) {
        logger.warn('ExtractedTieToFlatTie_ToSignalNotFound', {
            toSignalName: extractedTie.toSignalName,
            availableSignalNames: signals.map((s) => s.name),
        });
        return null;
    }
    if (extractedTie.connectionType === SignalTies_1.SignalTieConnectionType.Direct) {
        return {
            toSignalId,
            connectionType: extractedTie.connectionType,
            componentType: null,
            componentValue: null,
        };
    }
    if (!extractedTie.componentType || !extractedTie.componentValue) {
        logger.warn('ExtractedTieToFlatTie_MissingComponentData', { extractedTie });
        return null;
    }
    const characteristic = SignalTies_1.TIE_COMPONENT_TYPE_TO_CHARACTERISTIC[extractedTie.componentType];
    const siDefinition = sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions[characteristic];
    const componentValue = sigma_engine_shared_1.Utils.parseNumericalSIValue(extractedTie.componentValue, siDefinition);
    if (componentValue === undefined) {
        logger.warn('ExtractedTieToFlatTie_InvalidComponentValue', {
            componentValue: extractedTie.componentValue,
        });
        return null;
    }
    let variance = null;
    let varianceType = null;
    if (extractedTie.componentValueVariance) {
        const varianceProps = sigma_engine_shared_1.VarianceChecker.valueVarianceToProperties({ variance: extractedTie.componentValueVariance }, siDefinition);
        if (varianceProps === undefined) {
            logger.warn('ExtractedTieToFlatTie_InvalidVariance', {
                componentValueVariance: extractedTie.componentValueVariance,
            });
            return null;
        }
        const isRelativeVariance = varianceProps.units.short === sigma_engine_shared_1.CommonParameterUnits.percentage.short;
        varianceType = isRelativeVariance ? SignalTies_1.SignalTieVarianceType.Relative : SignalTies_1.SignalTieVarianceType.Absolute;
        variance = varianceProps.value;
    }
    return {
        toSignalId,
        connectionType: extractedTie.connectionType,
        componentType: extractedTie.componentType,
        componentValue: {
            type: 'absolute',
            value: componentValue,
            variance,
            varianceType,
        },
    };
}
const COMPONENT_TYPE_TO_UNIT = {
    [SignalTies_1.SignalTieComponentType.Capacitor]: sigma_engine_shared_1.CommonParameterUnits.capacitance,
    [SignalTies_1.SignalTieComponentType.Resistor]: sigma_engine_shared_1.CommonParameterUnits.resistance,
    [SignalTies_1.SignalTieComponentType.Inductor]: sigma_engine_shared_1.CommonParameterUnits.inductance,
};
function normalizeComponentValue(value, componentType) {
    if (!value || !componentType) {
        return value;
    }
    const unitDecl = COMPONENT_TYPE_TO_UNIT[componentType];
    if (!unitDecl) {
        return value;
    }
    try {
        const numericValue = sigma_engine_shared_1.Utils.parseNumericalSIValue(value, unitDecl);
        if (numericValue === undefined) {
            return value;
        }
        return sigma_engine_shared_1.Utils.formatNumericalSIValue(numericValue) + unitDecl.short;
    }
    catch {
        return value;
    }
}
function convertZeroComponentValueToNull(value, componentType) {
    if (!value || !componentType) {
        return value;
    }
    const unitDecl = COMPONENT_TYPE_TO_UNIT[componentType];
    if (!unitDecl) {
        return value;
    }
    try {
        const numericValue = sigma_engine_shared_1.Utils.parseNumericalSIValue(value, unitDecl);
        if (numericValue === 0) {
            return null;
        }
        return value;
    }
    catch {
        return value;
    }
}
function convertZeroValuesToNullInSignalTies(signalTies) {
    return signalTies.map((signal) => ({
        ...signal,
        ties: signal.ties.map((tie) => ({
            ...tie,
            componentValue: convertZeroComponentValueToNull(tie.componentValue, tie.componentType),
        })),
    }));
}
function createDeduplicationKey(signalName, tie) {
    const normalizedValue = normalizeComponentValue(tie.componentValue, tie.componentType);
    const valueKey = normalizedValue?.toLowerCase().replace(/\s+/g, '') ?? 'null';
    return [
        signalName.toLowerCase(),
        tie.toSignalName.toLowerCase(),
        tie.connectionType,
        tie.componentType ?? 'null',
        valueKey,
        tie.componentValueVariance ?? 'null',
    ].join('|');
}
function normalizeAndDeduplicateSignalTies(signalTies, directionRule) {
    return deduplicateSignalTies(directionRule.canonicalize(signalTies));
}
function deduplicateSignalTies(signalTies) {
    const tiesByKey = new Map();
    for (const signalTie of signalTies) {
        for (const tie of signalTie.ties) {
            const key = createDeduplicationKey(signalTie.signalName, tie);
            const existing = tiesByKey.get(key);
            if (existing) {
                existing.tie.evidence = [existing.tie.evidence, tie.evidence]
                    .filter((evidence) => evidence.trim().length > 0)
                    .join(' ; ');
            }
            else {
                tiesByKey.set(key, { signalName: signalTie.signalName, tie: { ...tie } });
            }
        }
    }
    const tiesBySignal = new Map();
    for (const { signalName, tie } of tiesByKey.values()) {
        const ties = tiesBySignal.get(signalName) ?? [];
        ties.push(tie);
        tiesBySignal.set(signalName, ties);
    }
    return [...tiesBySignal.entries()].map(([signalName, ties]) => ({ signalName, ties }));
}
//# sourceMappingURL=signal-ties-denormalized.js.map